import { chunkLoadStore } from '@web/libs/chunk-load-store'
import { type ComponentType, createElement } from 'react'

export type SheetOptions = {
  dismissible?: boolean
  snapPoints?: (number | string)[]
}

export type LazySheetEntry = {
  Component: ComponentType
  preload: () => Promise<void>
  options?: SheetOptions
}

export function lazySheet<K extends string, T extends Record<K, ComponentType>>(
  loader: () => Promise<T>,
  name: K,
  options?: SheetOptions,
): LazySheetEntry {
  let Component: ComponentType | null = null
  let loadPromise: Promise<ComponentType> | null = null
  let loadError: unknown = null

  const load = () => {
    if (Component) return Promise.resolve(Component)

    if (!loadPromise) {
      loadError = null
      loadPromise = chunkLoadStore.track(
        loader().then(
          (module) => {
            Component = module[name]
            return Component
          },
          (error: unknown) => {
            loadPromise = null
            loadError = error
            throw error
          },
        ),
      )
    }

    return loadPromise
  }

  const LazySheetComponent: ComponentType = (props) => {
    if (Component) return createElement(Component, props)

    if (loadError) throw loadError

    throw load()
  }

  return {
    Component: LazySheetComponent,
    preload: () => load().then(() => undefined),
    options,
  }
}
